import { useZodForm } from '@/components/form';
import { Sidebar, SidebarHeader, toggle, useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Blanket,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Form,
  FormattedRelativeTime,
  Modal,
  ModalBody,
  ModalFooter,
  PlainButton,
  Scrollable,
  Spinner,
  Text,
  TextField,
  Truncated,
  useConfirmationModal,
  useModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import plur from 'plur';
import { m, useReducedMotion } from 'motion/react';
import { useCopilotSessionId } from '@customerio/hydra-copilot/react';
import clsx from 'clsx';
import { z } from '@customerio/hydra-validation';
import { ENTRANCE_FADE_UP_ANIMATE, entranceFadeUpInitial, entranceStaggerTransition } from '@/utils/motion';
import { useCopilotConversations, useCopilotSessionContext } from './components/session-provider';
import { MAX_COPILOT_SESSION_NAME_LENGTH, useCopilotActions, useCopilotHistory } from './hooks';
import * as styles from './index.css';
import { isEmptyString } from '@/utils/string';

import { useIsAgentFullscreen, useNavigateToAgentChat } from './hooks';

const MotionHistoryListColumn = m.create(Box);

export function History() {
  const isActive = useSidebarActive('copilot-history');
  const { track } = useAnalytics();
  const isAgentFullscreen = useIsAgentFullscreen();
  const history = useCopilotHistory();
  const totalItems = history.data?.pages?.[0]?.meta?.pagination?.total ?? 0;
  const shouldReduceMotion = useReducedMotion();
  const historyPanelInitial = entranceFadeUpInitial(shouldReduceMotion);

  return (
    <Sidebar
      id="copilot-history"
      onToggle={(isOpen) => {
        if (isOpen) {
          track('aiAssistant', 'viewedHistory', {});
        }
      }}
    >
      <Box display="flex" flexDirection="column" h="full" w="full">
        <m.div
          initial={historyPanelInitial}
          animate={ENTRANCE_FADE_UP_ANIMATE}
          transition={entranceStaggerTransition(0, shouldReduceMotion)}
        >
          <SidebarHeader
            title="All conversations"
            leadingActions={
              !isAgentFullscreen ? (
                <Button
                  variant="tertiary"
                  size="sm"
                  icon="chevron-left"
                  isIconOnly={true}
                  onClick={() => {
                    toggle('copilot');
                  }}
                />
              ) : undefined
            }
            onClose={() => {
              toggle('copilot-history');
            }}
          />
        </m.div>

        {isActive && totalItems > 0 ? (
          <m.div
            initial={historyPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(1, shouldReduceMotion)}
          >
            <ChatCountBar total={totalItems} />
          </m.div>
        ) : null}

        {isActive ? (
          <MotionHistoryListColumn
            display="flex"
            flexDirection="column"
            flexGrow="1"
            minH="0"
            w="full"
            initial={historyPanelInitial}
            animate={ENTRANCE_FADE_UP_ANIMATE}
            transition={entranceStaggerTransition(isActive && totalItems > 0 ? 2 : 1, shouldReduceMotion)}
          >
            <Box px="100" flexGrow="1" h="0" display="flex" flexDirection="column">
              <Chats />
            </Box>
          </MotionHistoryListColumn>
        ) : null}
      </Box>
    </Sidebar>
  );
}

type ChatsLocation = 'sidebar' | 'fullscreen';

interface ChatsProps {
  location?: ChatsLocation;
}

export function Chats({ location = 'sidebar' }: ChatsProps) {
  const currentSession = useCopilotSessionContext();
  const { switchToConversation, removeConversation } = useCopilotConversations();
  const history = useCopilotHistory();
  const { track } = useAnalytics();
  const actions = useCopilotActions();
  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();
  const navigateToChat = useNavigateToAgentChat();
  const currentSessionId = useCopilotSessionId(currentSession);

  function continueSession(id: number) {
    track('aiAssistant', 'historyItemClicked', {});

    if (location === 'fullscreen') {
      navigateToChat({ sessionId: `${id}` });
    } else {
      switchToConversation(`${id}`);
      toggle('copilot');
    }
  }

  function deleteSession(id: number) {
    confirm({
      title: 'Delete Chat',
      message: 'Are you sure you want to delete this chat? This action cannot be undone.',
      isCritical: true,
      async onConfirm() {
        try {
          track('aiAssistant', 'historyItemDeleted', {});
          await actions.deleteSession(id);

          // Drops any live entry; a deleted active conversation is replaced with a fresh one.
          removeConversation(`${id}`);
        } catch (error) {
          sentryCaptureException(error);
          snackbars.create({
            variant: 'critical',
            message: 'Failed to delete chat history. Please try again.',
          });
        }
      },
    });
  }

  async function renameSession(id: number, name: string) {
    try {
      track('aiAssistant', 'historyItemRenamed', {});
      await actions.renameSession(id, name);

      if (currentSession.id === `${id}`) {
        currentSession.setSessionMeta({
          ...currentSession.__meta.get(),
          name,
        });
      }
    } catch (error) {
      sentryCaptureException(error);
      snackbars.create({
        variant: 'critical',
        message: 'Failed to rename chat history. Please try again.',
      });
    }
  }

  const totalItems = history.data?.pages?.[0]?.meta?.pagination?.total ?? -1;

  return (
    <Box flexGrow="1" h="0" display="flex" flexDirection="column" position="relative">
      {history.isError === true ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="critical">Failed to load history</EmptyStateTitle>
            <EmptyStateDescription>
              There was an error loading your chat history. Please try again.
            </EmptyStateDescription>
          </EmptyState>
        </Box>
      ) : history.isLoading || history.data == null ? (
        <Blanket>
          <Spinner />
        </Blanket>
      ) : totalItems === 0 ? (
        <Box h="full" display="flex" justifyContent="center" alignItems="center" pb="800">
          <EmptyState>
            <EmptyStateTitle icon="chat">No chats yet</EmptyStateTitle>
            <EmptyStateDescription>Start a chat with the AI agent</EmptyStateDescription>
            {location === 'sidebar' ? (
              <EmptyStateActions>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    currentSession.reset();
                    toggle('copilot');
                  }}
                >
                  Start Chat
                </Button>
              </EmptyStateActions>
            ) : null}
          </EmptyState>
        </Box>
      ) : (
        <Scrollable
          pt="0"
          px="0"
          pb="0"
          display="flex"
          flexDirection="column"
          overflow="y"
          h="full"
          onTouchEdge={(edge) => {
            if (edge === 'bottom' && history.isFetching === false) {
              history.fetchNextPage();
            }
          }}
        >
          {history.data.pages.flatMap((page) =>
            page.sessions.map((session) => (
              <HistoryItem
                key={session.id}
                id={session.id}
                isActive={currentSessionId === `${session.id}`}
                onClick={async () => {
                  continueSession(session.id);
                }}
                onRename={(name) => renameSession(session.id, name)}
                onDelete={() => deleteSession(session.id)}
                name={session.name}
                createdAt={session.created_at}
              />
            )),
          )}

          {history.isFetchingNextPage === true ? (
            <Box display="flex" justifyContent="center" p="100">
              <Spinner />
            </Box>
          ) : null}
        </Scrollable>
      )}
    </Box>
  );
}

type HistoryItemProps = {
  onClick: () => void;
  onRename: (name: string) => Promise<void>;
  onDelete: () => void;
  id: number;
  name: string | null;
  createdAt: number;
  isActive: boolean;
};

function HistoryItem({ onClick, onRename, onDelete, id, name, createdAt, isActive }: HistoryItemProps) {
  const renameModal = useRenameModal();

  return (
    <Box position="relative" className={clsx(styles.historyItem, isActive && styles.historyItemActive)}>
      <PlainButton onClick={onClick} className={styles.historyItemButton}>
        {(context) => (
          <>
            <Box as="span" className={context.isLoading === true ? styles.historyItemLoading : undefined}>
              <Text weight="medium" display="block" mr="400">
                <Truncated cursor="pointer">{!isEmptyString(name) ? name : `Chat ${id}`}</Truncated>
              </Text>
              <Text color="subtle" size="xs">
                Started <FormattedRelativeTime date={createdAt} cursor="pointer" shouldUppercaseFirst={false} />
              </Text>
            </Box>
            {context.isLoading === true ? (
              <Blanket>
                <Spinner />
              </Blanket>
            ) : null}
          </>
        )}
      </PlainButton>
      <DropdownMenu>
        <DropdownMenuTriggerButton
          className={styles.dropdownMenuTopRight}
          isIconOnly={true}
          icon="more"
          size="sm"
          variant="tertiary"
        />
        <DropdownMenuContent>
          <DropdownMenuItem
            onClick={() => {
              renameModal.open({ name: name ?? '', onSave: onRename });
            }}
          >
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem isCritical={true} onClick={() => onDelete()}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Box>
  );
}

const renameSchema = z.object({
  name: z
    .string()
    .min(1, 'Chat name is required')
    .max(MAX_COPILOT_SESSION_NAME_LENGTH, `Chat name must be at most ${MAX_COPILOT_SESSION_NAME_LENGTH} characters`),
});

type RenameModalData = {
  name: string;
  onSave: (name: string) => Promise<void>;
};

function RenameModal({ modalState, data, onClose }: PlumaManagedModalProps<RenameModalData>) {
  const { name, onSave } = data;
  const form = useZodForm({
    schema: renameSchema,
    defaultValues: { name },
    mode: 'onChange',
  });

  const { isValid, isSubmitting } = form.formState;

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSave(values.name);
    onClose();
  });

  return (
    <Modal state={modalState} onClose={onClose} size="sm" title="Rename Chat">
      <ModalBody>
        <Form id="rename-item" onSubmit={handleSubmit}>
          <TextField {...form.registerPluma('name')} label="Chat Name" placeholder="Enter a name for this chat" />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" form="rename-item" isDisabled={!isValid} isLoading={isSubmitting}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function useRenameModal() {
  return useModal(RenameModal);
}

type ChatCountBarProps = { total: number };

export function ChatCountBar({ total }: ChatCountBarProps) {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      pl="250"
      pr="200"
      pt="100"
      pb="100"
      style={{ borderBottom: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}` }}
    >
      <Text color="subtle" size="xs">
        {total} {plur('chat', total)}
      </Text>
      <Text color="subtle" size="xs">
        Stored for 30 days
      </Text>
    </Box>
  );
}
